import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { Checkbox } from "../../../components/ui/Checkbox";
import { useTranslation } from "react-i18next";
import { deleteProductService, getAllProductsService } from "redux/services/products_sevice";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

const ProductTable = ({
  products,
  selectedProducts,
  onProductSelect,
  onSelectAll,
  onProductEdit,
  onProductView,
  sortConfig,
  onSort,
}) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const [editingProduct, setEditingProduct] = useState(null);
  const [editValues, setEditValues] = useState({});

  const handleInlineEdit = (product) => {
    setEditingProduct(product.id);
    setEditValues({
      price: product.price,
      stock: product.quantity, // Use quantity as stock
    });
  };

  const handleSaveEdit = (productId) => {
    onProductEdit(productId, editValues);
    setEditingProduct(null);
    setEditValues({});
  };

  const handleCancelEdit = () => {
    setEditingProduct(null);
    setEditValues({});
  };

  const getStockStatusBadge = (stock, lowStockThreshold = 10) => {
    if (stock === 0) {
      return (
        <span className="px-4 py-1 text-xs font-medium bg-error text-error-foreground rounded-full">
          {t("outOfStock")}
        </span>
      );
    } else if (stock <= lowStockThreshold) {
      return (
        <span className="px-4 py-1 text-xs font-medium bg-warning text-warning-foreground rounded-full">
          {t("lowStock")}
        </span>
      );
    } else {
      return (
        <span className="px-4 py-1 text-xs font-medium bg-success text-success-foreground rounded-full">
          {t("inStock")}
        </span>
      );
    }
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) {
      return (
        <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />
      );
    }
    return sortConfig.direction === "asc" ? (
      <Icon name="ArrowUp" size={14} className="text-primary" />
    ) : (
      <Icon name="ArrowDown" size={14} className="text-primary" />
    );
  };

  const renderVariants = (variants) => {
    if (!variants || !variants.length) return "No variants";
    return variants.map((variant) => (
      <div key={variant.id}>
        {variant.values.map((value) => value.translations.en.value).join(", ")}
      </div>
    ));
  };

  return (
    <>
       <div className="hidden md:block overflow-x-auto">
       <table className="w-full text-sm text-gray-700">
        <thead className="bg-green-50 text-green-900 font-semibold">
            <tr>
              <th className="text-center p-4 text-sm font-medium text-foreground">
                {t("product")}
              </th>

              <th
                className="text-center p-4 text-sm font-medium text-foreground cursor-pointer hover:bg-muted transition-micro"
                onClick={() => onSort("category")}
              >
                <div className="flex items-center space-x-2">
                  <span>{t("category")}</span>
                </div>
              </th>
              <th
                className="text-center p-4 text-sm font-medium text-foreground cursor-pointer hover:bg-muted transition-micro"
                onClick={() => onSort("price")}
              >
                <div className="flex items-center space-x-2">
                  <span>{t("price")}</span>
                </div>
              </th>
              <th className="text-center p-4 text-sm font-medium text-foreground">
                {t("variants")}
              </th>
            
              <th className="text-center p-4 text-sm font-medium text-foreground">
                {t("status")}
              </th>
              <th className="text-center p-4 text-sm font-medium text-foreground">
                {t("actions")}
              </th>
            </tr>
          </thead>

          <tbody>
            {products.map((product) => (
              <tr
                key={product.id}
                className="border-b border-border hover:bg-muted/50 transition-micro"
              >
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-md overflow-hidden bg-surface">
                      <Image
                        src={
                          product.images.length > 0
                            ? product.images[0].file_path
                            : ""
                        } // Use the first image if available
                        alt={product.translations.en.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <button
                        onClick={() => onProductView(product)}
                        className="font-medium text-foreground hover:text-primary transition-micro text-center"
                      >
                        {product.translations.en.name}
                      </button>
                      <p className="text-sm text-muted-foreground">
                        {product.translations.en.desc}
                      </p>
                    </div>
                  </div>
                </td>

                <td className="p-4">
                  <span className="text-sm text-foreground">
                    {product.category.translations.en.name}
                  </span>
                </td>
                <td className="p-4">
                  {editingProduct === product.id ? (
                    <Input
                      type="number"
                      value={editValues.price}
                      onChange={(e) =>
                        setEditValues({
                          ...editValues,
                          price: parseFloat(e.target.value),
                        })
                      }
                      className="w-20"
                      step="0.01"
                    />
                  ) : (
                    <span className="font-medium text-foreground">
                      {product.price.toFixed(2)}
                    </span>
                  )}
                </td>
                <td className="p-4 text-center">
                  {renderVariants(product.variants)} {/* Render variants */}
                </td>
               
                <td className="p-4 text-center">{getStockStatusBadge(product.quantity)}</td>
                <td className="p-4">
                  <div className="flex items-center justify-end space-x-2">
                    {editingProduct === product.id ? (
                      <>
                        <button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleSaveEdit(product.id)}
                          title={t("saveChanges")}
                        >
                          <Icon
                            name="Check"
                            size={16}
                            className="text-success"
                          />
                        </button>
                        <button
                          variant="ghost"
                          size="icon"
                          onClick={handleCancelEdit}
                          title={t("cancelEditing")}
                        >
                          <Icon name="X" size={16} className="text-error" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                        className="mr-2 ml-2"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleInlineEdit(product)}
                          title={t("quickEdit")}
                        >
                          <Icon name="Edit2" size={16} />
                        </button>
                        <button
                          variant="ghost"
                          size="icon"
                          onClick={() => onProductView(product)}
                          title={t("viewDetails")}
                        >
                          <Icon name="Eye" size={16} />
                        </button>

                        <button
                          variant="outline"
                          size="sm"
                           onClick={() =>
                            dispatch(deleteProductService(product?.id)).then(
                              (response) => {
                                if (
                                  response?.payload?.status === 200 ||
                                  response?.payload?.status === 201
                                ) {
                                  toast("تم الحذف بنجاح");
                                  dispatch(getAllProductsService({ page: 1 }));
                                  //   onClose();
                                }
                              }
                            )
                          }
                          className="text-error hover:text-error"
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {products.length === 0 && (
        <div className="text-center py-12">
          <Icon
            name="Package"
            size={48}
            className="text-muted-foreground mx-auto mb-4"
          />
          <h3 className="text-lg font-medium text-foreground mb-2">
            {t("noProductsFound")}
          </h3>
          <p className="text-muted-foreground">{t("adjustSearch")}</p>
        </div>
      )}
    </>
  );
};

export default ProductTable;
